import { useEffect, useRef } from 'react'
import type { AsyncState } from '../async/Async'

/**
 * What "viewed" means for the screens that call this hook — `accounts_viewed`,
 * `categories_viewed`, `household_viewed`: **once per mount of the screen, at
 * the first render in which an answer is on screen** — status `success`,
 * `stale` OR `empty` — whether it came from the cache or the network. It counts
 * views, not cold loads. An empty list is an answer (`fire` gets `[]`, so
 * counts read 0): a brand-new household looking at "No accounts yet" is the top
 * of the link funnel and must not be invisible.
 *
 * It does NOT fire on: a repeat render, a background refetch, stale → success,
 * or loading → error. A screen that unmounts and mounts again is a new view.
 *
 * NOT covered, and different on purpose: `rules_viewed`
 * (`routes/rules/useViewedOnce.ts`, per tab, with its own payload) and
 * `transactions_viewed` (`routes/transactions/transactionsAnalytics.ts`, first
 * success only). They are not migrated here.
 *
 * Deliberately NOT `<Async announce>`: that callback runs only on a
 * loading → data EDGE, so a warm-cache mount (straight into success) emitted
 * nothing, and an analytics event shared a trigger with a screen-reader
 * affordance. The announcement can now change without touching events.
 *
 * `ready` (default `true`): a screen whose event payload depends on a SECOND
 * query that only starts after the first answers passes `false` until that
 * query has settled (success, empty, stale or error), and the hook holds the one
 * event until then. It gates only WHEN the event fires; every other rule above
 * is unchanged, and `ready` flipping back to false after firing does nothing.
 *
 * StrictMode: its simulated unmount/remount re-runs effects but PRESERVES refs,
 * so the `fired` ref set on the first run blocks the second. A genuine remount
 * gets a fresh ref and fires again.
 */
export function useScreenViewed<T>(
  state: AsyncState<readonly T[]>,
  fire: (items: readonly T[]) => void,
  ready = true,
): void {
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current || !ready) return
    if (state.status === 'empty') {
      fired.current = true
      fire([])
    } else if (state.status === 'success' || state.status === 'stale') {
      fired.current = true
      fire(state.data)
    }
  }, [state, fire, ready])
}
